import { isNullish } from '@y42/util/nullish';
import * as go from 'gojs';
import { getTopLeftObjectPoint } from '../../../../../utils/transform-node-bounds-to-html';

export class CustomTextEditor extends go.HTMLInfo {
  constructor() {
    super();
    this.textArea = this.createTextArea();

    this.textAreaEventListeners = new Map([
      ['input', this.onInput],
      ['keydown', this.onKeyDown],
      ['focus', this.onFocus],
      ['blur', this.onBlur],
    ]);

    this.show = this._show;
    this.hide = this._hide;
    this.valueFunction = this._valueFunction;
  }

  flexibleWidth = false;
  textAreaEventListeners: Map<string, (e: Event) => void>;

  private readonly defaultLineHeight = 1.18;
  private needSizeRecalculation = false;
  private tool: go.TextEditingTool | null = null;
  private readonly textArea: HTMLTextAreaElement;

  private readonly _show = (textBlock: go.GraphObject, diagram: go.Diagram, tool: go.Tool) => {
    if (!(textBlock instanceof go.TextBlock)) {
      return;
    }
    this.tool = tool as go.TextEditingTool;
    this.registerEventListeners(diagram);
    this.textArea.value = textBlock.text;

    this.initTextAreaStyle(textBlock, diagram);

    diagram.div?.appendChild(this.textArea);
    this.textArea.style.height = `${this.textArea.scrollHeight}px`;
    this.textArea.focus();
  };

  private readonly _hide = (diagram: go.Diagram) => {
    if (diagram.div?.contains(this.textArea)) {
      diagram.div.removeChild(this.textArea);
    }

    this.removeEventListeners(diagram);
    this.tool = null;
  };

  private readonly _valueFunction = () => this.textArea.value;

  private registerEventListeners(diagram: go.Diagram) {
    this.textAreaEventListeners.forEach((val, key) => this.textArea.addEventListener(key, val));
    diagram.addModelChangedListener(this.onModelChanged);
  }

  private removeEventListeners(diagram: go.Diagram) {
    this.textAreaEventListeners.forEach((val, key) => this.textArea.removeEventListener(key, val));
    diagram.removeModelChangedListener(this.onModelChanged);
  }

  private brushToCssColor(brushLike: go.BrushLike, defaultColor: string) {
    return brushLike instanceof go.Brush ? brushLike?.color : brushLike?.toString() || defaultColor;
  }

  private readonly onInput = () => {
    if (!this.tool?.textBlock) {
      return;
    }

    this.tool.textBlock.text = this.textArea.value;

    if (this.flexibleWidth) {
      this.tool.textBlock.part?.ensureBounds();
      const { width } = this.tool.textBlock.measuredBounds;
      this.textArea.style.width = `${width * this.tool.diagram.scale}px`;
    }

    this.textArea.style.height = 'auto';
    this.textArea.style.height = `${this.textArea.scrollHeight}px`;
  };

  private readonly onKeyDown = (e: Event) => {
    const tool = this.tool;
    if (isNullish(tool) || isNullish(tool?.textBlock)) {
      return;
    }

    switch ((e as KeyboardEvent).key) {
      case 'Enter':
        if (tool.textBlock.isMultiline === false) {
          e.preventDefault();
        }
        tool.acceptText(go.TextEditingTool.Enter);
        return;
      case 'Tab':
        tool.acceptText(go.TextEditingTool.Tab);
        e.preventDefault();
        return;
      case 'Escape':
        tool.doCancel();
        if (tool.diagram !== null) {
          tool.diagram.doFocus();
        }
        return;
    }
  };

  private readonly onFocus = () => {
    const tool = this.tool;
    if (!tool?.currentTextEditor) {
      return;
    }

    if (tool.state === go.TextEditingTool.StateActive) {
      tool.state = go.TextEditingTool.StateEditing;
    }
  };

  private readonly onBlur = () => {
    const tool = this.tool;
    if (!tool?.currentTextEditor || tool.state === go.TextEditingTool.StateNone) {
      return;
    }

    this.textArea.focus();
  };

  private readonly onModelChanged = (e: go.ChangedEvent) => {
    if (
      isNullish(this.tool) ||
      isNullish(this.tool?.diagram) ||
      isNullish(this.tool?.textBlock) ||
      isNullish(this.tool?.textBlock.part) ||
      isNullish(this.tool?.textBlock.part.key) ||
      (!e.isTransactionFinished && e.object?.key !== this.tool.textBlock.part.key)
    ) {
      return;
    }

    switch (e.propertyName) {
      case 'font':
        if (this.tool.diagram.div) {
          this.tool.diagram.div.style.font = e.newValue;
        }
        this.needSizeRecalculation = true;
        break;
      case 'textAlignment':
        this.textArea.style.textAlign = e.newValue;
        break;
      case 'textColor':
        this.textArea.style.color = this.brushToCssColor(e.newValue, 'black');
        this.tool.textBlock.stroke = 'transparent';
        break;
      default:
        break;
    }

    if (e.isTransactionFinished && this.needSizeRecalculation) {
      this.recalculateTextAreaSize();
    }
  };

  private recalculateTextAreaSize() {
    if (!this.tool || !this.tool.textBlock) {
      return;
    }
    const {
      lineCount,
      actualBounds: { height, width },
    } = this.tool.textBlock;
    this.textArea.style.lineHeight = `${
      (height / (lineCount || 1)) * this.tool.diagram.scale || this.defaultLineHeight
    }px`;
    if (this.flexibleWidth) {
      this.textArea.style.width = `${width * this.tool.diagram.scale}px`;
    }
    this.textArea.style.height = 'auto';
    this.textArea.style.height = `${this.textArea.scrollHeight}px`;
  }

  private initTextAreaStyle(textBlock: go.TextBlock, diagram: go.Diagram) {
    if (diagram.div) {
      diagram.div.style.font = textBlock.font;
    }
    this.textArea.style.color = this.brushToCssColor(textBlock.stroke, 'black');
    const { x, y } = getTopLeftObjectPoint(textBlock);
    const {
      lineCount,
      actualBounds: { height },
    } = textBlock;

    this.textArea.style.fontSize = `${diagram.scale * 100}%`;
    this.textArea.style.width = `${textBlock.naturalBounds.width * diagram.scale}px`;
    this.textArea.style.height = 'auto';
    this.textArea.style.left = `${x}px`;
    this.textArea.style.top = `${y}px`;
    this.textArea.style.lineHeight = `${(height / (lineCount || 1)) * diagram.scale || this.defaultLineHeight}px`;
    this.textArea.style.textAlign = textBlock.textAlign;
    this.textArea.style.transform = `rotate(${textBlock.getDocumentAngle()}deg)`;
  }

  private createTextArea(): HTMLTextAreaElement {
    const textArea = document.createElement('textarea');
    textArea.id = 'gojsTextArea';

    textArea.style.position = 'fixed';
    textArea.style.zIndex = '10';
    textArea.style.font = 'inherit';
    textArea.style.padding = '0';
    textArea.style.margin = '0';
    textArea.style.border = '0';
    textArea.style.outline = 'none';
    textArea.style.whiteSpace = 'pre-wrap';
    textArea.style.overflow = 'hidden'; // for proper IE wrap
    textArea.style.background = 'transparent';
    textArea.style.resize = 'none';
    textArea.style.boxSizing = 'border-box';
    textArea.style.height = 'auto';
    textArea.style.transformOrigin = '0 0';

    textArea.className = 'goTXarea';

    return textArea;
  }
}
